import { useEffect, useState, useRef } from "react";
import { ArrowLeft, Send, Paperclip, BarChart, BarChart2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

// Sample conversation data for insights channel
const conversationData = [
  {
    id: 1,
    sender: "AnalyticsBot",
    avatarSrc: "/crew-agents/webanalytics.png",
    message: "I've completed the monthly insights report for July 2023. Here are the key findings across all domains:",
    timestamp: "Today at 9:15 AM",
    isUser: false,
    attachment: {
      type: "report",
      title: "July 2023 Performance Insights"
    }
  },
  {
    id: 2,
    sender: "ContentBot",
    avatarSrc: "/crew-agents/content-generator.jpg",
    message: "Thanks for the report. I've correlated this with content performance data and found that our how-to guides are showing a 34% higher engagement rate compared to other content types.",
    timestamp: "Today at 9:22 AM",
    isUser: false
  },
  {
    id: 3,
    sender: "AnalyticsBot",
    avatarSrc: "/crew-agents/webanalytics.png",
    message: "That's interesting - the data also shows mobile users spend 27% more time on how-to guides than desktop users. This might suggest we should optimize more of our content for mobile reading experiences.",
    timestamp: "Today at 9:25 AM",
    isUser: false
  },
  {
    id: 4,
    sender: "You",
    avatarSrc: "/crew-agents/page-semantic.png",
    message: "Great insights. Can we compile these findings and create some actionable recommendations for the content team?",
    timestamp: "Today at 9:30 AM",
    isUser: true
  },
  {
    id: 5,
    sender: "AnalyticsBot",
    avatarSrc: "/crew-agents/webanalytics.png",
    message: "I'll work with ContentBot to prepare a recommendations document. We'll focus on content format, length, and mobile optimization strategies based on our findings.",
    timestamp: "Today at 9:35 AM",
    isUser: false
  },
  {
    id: 6,
    sender: "CompetitorBot",
    avatarSrc: "/crew-agents/competitors.png",
    message: "I should note that two of our main competitors have recently increased their production of how-to content by about 40% in the last quarter, according to my latest analysis. This appears to be an industry trend worth investing in.",
    timestamp: "Today at 9:40 AM",
    isUser: false
  }
];

// Team members for this channel
const teamMembers = [
  {
    name: "AnalyticsBot",
    role: "Analytics Specialist",
    avatarSrc: "/crew-agents/webanalytics.png",
    status: "online"
  },
  {
    name: "ContentBot",
    role: "Content Analyzer",
    avatarSrc: "/crew-agents/content-generator.jpg", 
    status: "online"
  },
  {
    name: "CompetitorBot",
    role: "Competitor Intelligence",
    avatarSrc: "/crew-agents/competitors.png",
    status: "away"
  },
  {
    name: "You",
    role: "Marketing Director",
    avatarSrc: "/crew-agents/page-semantic.png",
    status: "online"
  }
];

// Recent insights
const recentInsights = [
  {
    id: 1,
    title: "Content Performance Analysis",
    description: "How-to guides outperform other formats by 34%",
    agent: "ContentBot",
    date: "Today"
  },
  {
    id: 2,
    title: "Mobile vs Desktop Engagement",
    description: "27% higher engagement on mobile for tutorials",
    agent: "AnalyticsBot",
    date: "Today"
  },
  {
    id: 3,
    title: "Competitor Content Strategy",
    description: "40% increase in tutorial content across industry",
    agent: "CompetitorBot",
    date: "Today"
  },
  {
    id: 4,
    title: "Weekly Performance Summary",
    description: "Traffic up 12%, conversions up 8%",
    agent: "AnalyticsBot",
    date: "Yesterday"
  }
];

// Key performance insights
const kpiInsights = [
  {
    metric: "Avg. Session Duration",
    value: "4:32",
    trend: "+12%",
    insight: "Users spending more time on how-to content"
  },
  {
    metric: "Mobile Conversion Rate",
    value: "3.2%",
    trend: "+8%",
    insight: "Mobile optimizations showing results"
  },
  {
    metric: "Content Engagement",
    value: "68%",
    trend: "+15%",
    insight: "Interactive elements increasing engagement"
  },
  {
    metric: "Return Visitors",
    value: "42%",
    trend: "+5%",
    insight: "Content quality driving repeat visits"
  }
];

const ChatMessage = ({ message }: { message: any }) => {
  const isUser = message.isUser;
  
  return (
    <div className={`flex gap-3 mb-4 ${isUser ? 'justify-end' : ''}`}>
      {!isUser && (
        <img 
          src={message.avatarSrc} 
          alt={message.sender}
          className="h-8 w-8 rounded-full object-cover mt-1"
        />
      )}
      
      <div className={`max-w-[70%] ${isUser ? 'bg-primary text-primary-foreground' : 'bg-muted'} rounded-lg px-4 py-2`}>
        <div className="flex justify-between items-baseline mb-1">
          <span className={`font-medium text-sm ${isUser ? 'text-primary-foreground' : 'text-foreground'}`}>
            {message.sender}
          </span>
          <span className={`text-xs ml-4 ${isUser ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
            {message.timestamp}
          </span>
        </div>
        <p className={`text-sm whitespace-pre-line ${isUser ? 'text-primary-foreground' : 'text-foreground'}`}>
          {message.message}
        </p>
        
        {message.attachment && (
          <div className={`mt-2 p-2 rounded border ${
            isUser ? 'border-white/20 bg-white/10' : 'border-gray-200 bg-background'
          }`}>
            <div className="flex items-center gap-2">
              <BarChart2 className="h-4 w-4 text-blue-500" />
              <span className="font-medium text-xs">{message.attachment.title}</span>
            </div>
          </div>
        )}
      </div>
      
      {isUser && (
        <img 
          src={message.avatarSrc} 
          alt={message.sender}
          className="h-8 w-8 rounded-full object-cover mt-1"
        />
      )}
    </div>
  );
};

const InsightCard = ({ insight }: { insight: any }) => {
  return (
    <div className="border rounded-md p-3 bg-background mb-2 hover:bg-muted/50 transition-colors cursor-pointer">
      <div className="flex justify-between mb-1">
        <h3 className="font-medium text-sm">{insight.title}</h3>
        <span className="text-xs text-muted-foreground">{insight.date}</span>
      </div>
      <p className="text-xs text-muted-foreground mb-2">{insight.description}</p>
      <div className="flex items-center gap-2 text-xs">
        <div className="flex items-center gap-1">
          <img 
            src={
              insight.agent === "ContentBot" ? "/crew-agents/content-generator.jpg" :
              insight.agent === "AnalyticsBot" ? "/crew-agents/webanalytics.png" :
              "/crew-agents/competitors.png"
            } 
            alt={insight.agent}
            className="h-4 w-4 rounded-full"
          />
          <span>{insight.agent}</span>
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ kpi }: { kpi: any }) => {
  return (
    <div className="border rounded-md p-3 bg-background hover:shadow-sm transition-shadow">
      <div className="flex justify-between mb-1">
        <span className="text-xs text-muted-foreground">{kpi.metric}</span>
        <span className="text-xs text-green-600 font-medium">{kpi.trend}</span>
      </div>
      <div className="text-lg font-bold mb-2">{kpi.value}</div>
      <p className="text-xs text-muted-foreground line-clamp-2">{kpi.insight}</p>
    </div>
  );
};

const InsightsChannelPage = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [conversation, setConversation] = useState(conversationData);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    document.title = "Insights Channel | PageMind Admin";
    
    // Scroll to bottom of messages
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (message.trim()) {
      // Add user message
      const newUserMessage = {
        id: conversation.length + 1,
        sender: "You",
        avatarSrc: "/crew-agents/page-semantic.png",
        message,
        timestamp: "Just now",
        isUser: true
      };
      
      setConversation([...conversation, newUserMessage]);
      setMessage("");
      
      // Simulate response after a delay
      setTimeout(() => {
        const botResponse = {
          id: conversation.length + 2,
          sender: "AnalyticsBot",
          avatarSrc: "/crew-agents/webanalytics.png",
          message: "We'll have the recommendations document ready within an hour. Is there any specific area you'd like us to focus on more deeply?",
          timestamp: "Just now",
          isUser: false
        };
        
        setConversation(prev => [...prev, botResponse]);
      }, 1000);
    }
  };

  return (
    <div className="container mx-auto p-6 h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 pb-4 border-b">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/insights/channels')}
            className="mr-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center">
            <div className="flex items-center gap-1 bg-primary/10 rounded-full px-3 py-1.5">
              <BarChart className="h-5 w-5 text-primary" />
              <div>
                <div className="font-bold flex items-center gap-2">
                  Insights
                  <Badge variant="outline" className="text-xs bg-primary/5">Team Channel</Badge>
                  <Badge className="bg-blue-500">2 Unread</Badge>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div>
          <Button variant="outline" size="sm">
            Channel Settings
          </Button>
        </div>
      </div>
      
      {/* Performance KPIs */}
      <div className="mb-4">
        <h3 className="text-sm font-medium mb-2 flex items-center gap-1">
          <Info className="h-4 w-4 text-blue-500" />
          Key Performance Insights
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {kpiInsights.map((kpi, index) => (
            <KpiCard key={index} kpi={kpi} />
          ))}
        </div>
      </div>
      
      {/* Main content - split into chat and sidebar */}
      <div className="flex gap-4 flex-1 overflow-hidden">
        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-background/80 rounded-lg">
          {conversation.map(msg => (
            <ChatMessage key={msg.id} message={msg} />
          ))}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Sidebar */}
        <div className="w-64 flex-shrink-0 hidden lg:block space-y-4">
          <div className="border rounded-md p-3 bg-background">
            <h3 className="font-medium mb-3">Recent Insights</h3>
            <div className="space-y-2">
              {recentInsights.map(insight => (
                <InsightCard key={insight.id} insight={insight} />
              ))}
            </div>
          </div>
          
          <div className="border rounded-md p-3 bg-background">
            <h3 className="font-medium mb-3">Team Members</h3>
            <div className="space-y-3">
              {teamMembers.map((member, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="relative">
                    <Avatar>
                      <AvatarImage src={member.avatarSrc} alt={member.name} />
                      <AvatarFallback>{member.name[0]}</AvatarFallback>
                    </Avatar>
                    <div className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-background ${
                      member.status === 'online' ? 'bg-green-500' : 
                      member.status === 'away' ? 'bg-yellow-500' : 'bg-gray-400'
                    }`} />
                  </div>
                  <div>
                    <div className="text-sm font-medium">{member.name}</div>
                    <div className="text-xs text-muted-foreground">{member.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Message Input */}
      <div className="mt-4">
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="rounded-full"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          
          <Input
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Message #insights..."
            className="flex-1"
          />
          
          <Button
            type="submit"
            disabled={!message.trim()}
          >
            <Send className="h-4 w-4 mr-2" />
            Send
          </Button>
        </form>
      </div>
    </div>
  );
};

export default InsightsChannelPage;